import React, { useState, useEffect } from 'react';
import { Ban, DollarSign, CheckCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { getGoogleAdsClient, getDateRange } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type NegativeKeyword } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

export default function NegativeKeywordManagerPage() {
  const [negatives, setNegatives] = useState<NegativeKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => {
    if (!company) return;
    loadNegatives();
  }, [company]);

  const loadNegatives = async () => {
    try {
      setLoading(true);
      const { data: mappings } = await supabase.from('google_ads_customer_mapping').select('*').eq('company', company).eq('is_active', true).limit(1).maybeSingle();
      if (!mappings) { toast.error(`No account connected for ${company}`); return; }

      const client = getGoogleAdsClient(mappings.customer_id);
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(mappings.customer_id, getDateRange(30));
      setNegatives(report.negativeKeywords);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (term: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(term) ? next.delete(term) : next.add(term);
      return next;
    });
  };

  const addNegatives = async () => {
    toast.success(`Would add ${selected.size} negative keywords (dry run)`);
  };

  const totalWaste = negatives.reduce((sum, n) => sum + n.wastedSpend, 0);

  if (loading) {
    return <div className="p-8"><div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-blue-500" /><span className="ml-3">Loading...</span></div></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Negative Keyword Manager</h1>
      <p className="text-gray-600 mb-8">Stop wasting money on irrelevant searches</p>
      <div className="flex justify-end mb-6">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Candidates</span>
            <Ban className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold">{negatives.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Wasted Spend</span>
            <DollarSign className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600">${totalWaste.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Selected</span>
            <CheckCircle className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{selected.size}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex justify-between">
        <button onClick={() => setSelected(new Set(negatives.map(n => n.searchTerm)))} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Select All</button>
        <button onClick={addNegatives} disabled={selected.size === 0} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300">
          Add {selected.size} Negative Keywords
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Negative Keyword Candidates ({negatives.length})</h2></div>
        <div className="divide-y">
          {negatives.map((neg, idx) => (
            <div key={idx} className={`p-6 ${selected.has(neg.searchTerm) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
              <div className="flex items-start gap-4">
                <input type="checkbox" checked={selected.has(neg.searchTerm)} onChange={() => toggleSelect(neg.searchTerm)} className="mt-1 h-5 w-5" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{neg.searchTerm}</h3>
                  <p className="text-sm text-gray-700 mb-2">{neg.reason}</p>
                  <div className="flex gap-4 text-sm">
                    <span>Wasted: <span className="font-semibold text-red-600">${neg.wastedSpend.toFixed(2)}</span></span>
                    <span>Clicks: {neg.clicks}</span>
                    <span>Impressions: {neg.impressions}</span>
                    <span>Confidence: {(neg.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
